import React from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { SchoolHelper } from '@laputan/helpers'

import { SCREEN_SIZE, STYLES } from '../../static/style'
import { ScreenSizeHelper } from '../../helpers/ScreenSizeHelper'
import { SchoolLogo } from '../SchoolLogo'
import { Text } from '../../components/Text'
import { LevelBadge } from '../Badge/LevelBadge'
import { SectorBadge } from '../Badge/SectorBadge'
import { GenderBadge } from '../Badge/GenderBadge'
import { SchoolLocationMap } from '../SchoolLocationMap'
import { PhoneIcon } from '../../components/Icon/PhoneIcon'
import { WebsiteIcon } from '../../components/Icon/WebsiteIcon'
import { SchoolOverviewButton } from '../Button/SchoolOverviewButton'
import { getWindow } from '../../helpers/UtilHelper'
import { Link } from '../../components/Link'
import { GoogleDirectionsLink } from '../../components/Link/GoogleDirectionsLink'
import { SchoolResultBadgeGroup } from '../Badge/SchoolResultBadgeGroup'
import { CONFIG } from '../../config'
import { SocialMediaShareButtons } from '../../components/Button/SocialMediaShareButtons'
import { School } from '../props/School.types'
import { showEditSchoolModal } from '../../states/payments/actions'
import { ScreenSize } from '../../states/screenSize/types'
import AcademicRatingBadge from '../Badge/AcademicRatingBadge'

const StyledSchoolOverview = styled.div`
  position: relative;
  margin: 1rem 0 0;

  ${SCREEN_SIZE.MD} {
    margin: 0;
  }
`

const SchoolName = styled.h1`
  margin: 0 0 1rem;
  font-size: ${STYLES.FONT_SIZE_XL};
`

const SchoolOverviewInformation = styled.div`
  margin: 0 ${STYLES.GUTTER};
  padding: 1rem 0.5rem;
  background-color: ${STYLES.COLOR_WHITE};
  max-width: 50rem;
  overflow: hidden;

  ${SCREEN_SIZE.MD} {
    margin: 1rem;
    display: inline-block;
    position: relative;
    z-index: 3;
    padding: 2rem 2rem 2rem;
    max-width: calc(${STYLES.CONTENT_MAX_WIDTH} / 2);
    margin-left: 50%;
    transform: translateX(-100%);
    box-sizing: border-box;
    border-radius: 1rem;
    box-shadow: 0 0 1rem ${STYLES.COLOR_OUTER_SPACE_GRAY};
    ${({ isFeatured }: { isFeatured: boolean }) =>
      isFeatured &&
      `
      border: 2px solid ${STYLES.PRIMARY_COLOR};
    `}
  }
`

const StyledSchoolLocationMap = styled(SchoolLocationMap)`
  ${SCREEN_SIZE.MD} {
    height: auto;
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
  }
`

const StyledSchoolLogo = styled(SchoolLogo)`
  float: right;
`

const AddressText = styled(Text)`
  color: ${STYLES.COLOR_PALE_SKY_GRAY};
`

const AddressTextLink = styled(Link)`
  display: inline;
  font-weight: ${STYLES.FONT_WEIGHT_NORMAL};
`

const StyledLevelBadge = styled(LevelBadge)``

const StyledSectorBadge = styled(SectorBadge)``

const StyledGenderBadge = styled(GenderBadge)``

const SchoolOverviewButtonsContainer = styled.div`
  margin-top: 1rem;
`

const Location = styled.div`
  display: block;
  margin-bottom: 1.8rem;
`

const SchoolResultBadgeGroupContainer = styled.div``

const EditText = styled.div`
  display: flex;
  margin-top: -1rem;
  color: ${STYLES.PRIMARY_COLOR};
  font-size: ${STYLES.FONT_SIZE_SM};
`

const SchoolOverview = ({ school }: { school: School }) => {
  const dispatch = useDispatch()
  const {
    logo,
    name,
    level,
    sector,
    gender,
    location,
    contact,
    results,
    isFeatured,
    mathsRating,
    englishRating,
  } = school
  const {
    street,
    suburb,
    state,
    postcode,
    coords: [lng, lat],
  } = location || {}
  const { phone, website } = contact || {}
  let websiteWithoutProtocol
  if (website) {
    websiteWithoutProtocol = website.replace(/^http(s)*:\/\//g, '')
  }
  const { size: screenSize } = useSelector(({ screenSize: ss }) => ss)
  const displayablePhoneNumber = SchoolHelper.getDisplayablePhoneNumber(phone)
  let callButtonText = `Call ${phone}`
  return (
    <StyledSchoolOverview>
      {ScreenSizeHelper.isBreakpointGreaterAndEqual(
        screenSize,
        ScreenSize.MD
      ) && (
        <StyledSchoolLocationMap
          schoolName={name}
          location={location}
          moveXBy={ScreenSizeHelper.getScreenSizeValueFromObject(screenSize, {
            default: -0.004,
            MD: -0.006,
          })}
        />
      )}
      <SchoolOverviewInformation isFeatured={isFeatured}>
        {logo && <StyledSchoolLogo alt={name} logo={logo} size={100} />}
        <SchoolName>{name}</SchoolName>
        <EditText>
          (
          <Link
            onClick={() => {
              dispatch(showEditSchoolModal(school))
            }}
          >
            edit
          </Link>
          )
        </EditText>
        <Location>
          {street && <AddressText>{`${street}, `}</AddressText>}
          {suburb && state && postcode && (
            <AddressTextLink
              to={SchoolHelper.getSearchUrl({
                search: `suburb:"${suburb}" state:"${state}"`,
              })}
              title={`Search for ${suburb}, ${state} ${postcode}`}
            >
              {`${suburb}, ${state} ${postcode}`}
            </AddressTextLink>
          )}
          {lat && lng && (
            <>
              <br />
              <GoogleDirectionsLink coords={{ lat, lng }} />
            </>
          )}
        </Location>
        <StyledLevelBadge>{level}</StyledLevelBadge>
        <StyledSectorBadge>{sector}</StyledSectorBadge>
        <StyledGenderBadge>{gender}</StyledGenderBadge>
        <SchoolResultBadgeGroupContainer>
          <SchoolResultBadgeGroup
            results={results}
            mathsRating={mathsRating}
            englishRating={englishRating}
          />
        </SchoolResultBadgeGroupContainer>
        <SchoolOverviewButtonsContainer>
          {displayablePhoneNumber && (
            <SchoolOverviewButton
              title={callButtonText}
              label={callButtonText}
              icon={<PhoneIcon />}
              onClick={() => {
                getWindow().location.href = `tel:${phone}`
              }}
            >
              {displayablePhoneNumber}
            </SchoolOverviewButton>
          )}
          {/*
          {email && (
            <SchoolOverviewButton
              title={`Send an email to ${email}`}
              icon={<EmailIcon />}
              onClick={() => (getWindow().location.href = `mailto:${email}`)}
            >
              Email
            </SchoolOverviewButton>
          )}
*/}
          {websiteWithoutProtocol && (
            <SchoolOverviewButton
              title={`Go to ${websiteWithoutProtocol}`}
              icon={<WebsiteIcon />}
              onClick={() => {
                getWindow().open(`http://${websiteWithoutProtocol}`, '_blank')
              }}
            >
              Website
            </SchoolOverviewButton>
          )}
        </SchoolOverviewButtonsContainer>
        <SocialMediaShareButtons
          url={`${
            CONFIG.WEBSITE_SITEURL
          }${SchoolHelper.normalizeSchoolUrlHandle(school, true)}`}
          title={`${school.name} | AroundSchools`}
        />
      </SchoolOverviewInformation>
    </StyledSchoolOverview>
  )
}

export { SchoolOverview }
